import { Report, ReportResponse } from "services/types/reports.type";
import {
  convertCustomTestResponseToModel,
  convertReportResponseToModel
} from "utils/reports.utils";

import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import { Diagnosis } from "./types/diagnosis.type";
import type { PromptOutputFormat } from "./types/prompts.type";
import {
  CombineReportsInput,
  CustomTest,
  CustomTestInput,
  CustomTestResponse,
  ReportFilters,
  ReportInput,
  ReportSummaryInstructions,
  ReportsResponse
} from "./types/reports.type";

export const GET_LIST_OF_REPORTS_DEFAULT_QUERY =
  "{id,type,is_signed,created_at,clinic{id,name,display_name},noah_session{session_date},signed_at,appointment{id,start_moment,clinic{name,display_name,timezone},staff_member{id,first_name,last_name,suffix,title},type{id,name}},report_date,created_by}";
export const GET_REPORT_DEFAULT_QUERY =
  "{id,otoacoustic_emissions_report,otoscopy_report,real_ear_measurement_report,test_box_report,pure_tone_audiogram_report,speech_audiometry_report,word_recognition_report,additional_word_recognition_report,tympanometry_report,acoustic_reflex_report,acoustic_reflex_decay_report,speech_in_noise_report,audiometer,calibration_date,audiometer_configuration{id,audiometer,calibration_date,serial_number,title,is_active},referring_physician,primary_care_physician,physicians,summary,cover_letter,include_cover_letter,type,reliability,test_method,transducer,hearing_loss_left,hearing_loss_right,hearing_loss_type_left,hearing_loss_type_right,hearing_loss_shape_left,hearing_loss_shape_right,is_diagnosis_selected,is_test_configuration_selected,is_hearing_aids_included,signed_at,is_signed,report_date,created_at,clinic{id,display_name,name,logo,timezone,street_address_1,street_address_2,city,state,zip_code,phone,practice{id,name,logo,timezone}},noah_session{id,session_date},appointment{id,clinic{id,display_name,name,logo,timezone,street_address_1,street_address_2,city,state,zip_code,phone,practice{id,name,logo,timezone}},start_moment,patient{noah_id,noah_patient_id},staff_member{id,first_name,last_name,suffix,title,signature,license_number,resolved_license_number,state_licenses{state,license_number}},supervising_provider{id,first_name,last_name,suffix,title,signature,license_number,resolved_license_number,state_licenses{state,license_number}},is_direct_supervisor,type{id,name,default_duration}},reviewer{id,first_name,last_name,suffix,title,signature,license_number,resolved_license_number,state_licenses{state,license_number}},diagnosis{id,hearing_loss_left,hearing_loss_right,hearing_loss_type_left,hearing_loss_type_right,hearing_loss_shape_left,hearing_loss_shape_right,icd10_codes,recommended_icd10_codes},include_tympanometry_graph,tympanometry_scale_max,tympanometry_scale_step}";

// The report page always reads the session id (the separate-report cards need it); the list does not.
const NOAH_SESSION_DEFAULT_READ = /noah_session\{(?:id,)?session_date\}/;

export const REPORT_NOAH_SESSION_FIELDS =
  "id,session_date,noah_actions{module_id}";
export const REPORT_LIST_NOAH_SESSION_FIELDS = "id,session_date";

// Off, the query is returned unchanged, so a backend that predates the feature never sees a key it
// would reject.
export const withNoahSessionFields = (
  query: string,
  enabled: boolean,
  sessionFields: string
): string =>
  enabled
    ? query.replace(
        NOAH_SESSION_DEFAULT_READ,
        `noah_session{${sessionFields}},noah_sync_failed_at`
      )
    : query;

// Minimal field set for the patient header / quickview / inbox-panel "last audiogram"
// summary. Only includes what those surfaces render: the report date, the pure-tone
// audiogram graph, and the speech/word-recognition tooltip. Avoids pulling the full
// report (all sub-reports + nested clinic/practice/appointment/reviewer/diagnosis),
// which is unnecessary outside the audiology section.
export const PATIENT_HEADER_AUDIOGRAM_QUERY =
  "{id,report_date,created_at,pure_tone_audiogram_report,speech_audiometry_report,word_recognition_report,additional_word_recognition_report,noah_session{session_date},appointment{start_moment,clinic{timezone}}}";

// ENG-1601: getReport used to always provide the broad "Reports" tag, so
// any list-level invalidation (adding/deleting a report, a test section
// unmounting, etc.) refetched the open report and reset its section forms,
// wiping unsaved edits. scopedTags (rollout flag: audiologyReportDetailTagScope)
// narrows the tag to the report's own id so only genuinely-relevant events
// (Noah sync, sign flow, explicit refetch) can refresh an open detail.
export const getReportProvidesTags = (
  result: Report | undefined,
  arg: { scopedTags?: boolean }
) =>
  arg.scopedTags
    ? [{ type: "Report" as const, id: result?.id }]
    : ["Reports" as const, { type: "Report" as const, id: result?.id }];

export const reportsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getReport: builder.query<
      Report,
      { reportId: number; query: string; scopedTags?: boolean }
    >({
      query: ({ reportId, query = "{*}" }) => ({
        url: `/reports/${reportId}/?query=${query}`,
        headers: { "X-Reports-DB": "true" }
      }),
      providesTags: (result, _error, arg) => getReportProvidesTags(result, arg),
      transformResponse: convertReportResponseToModel,
      transformErrorResponse: (error, _meta, arg) => {
        console.error("[getReport] request failed", {
          reportId: arg?.reportId,
          query: arg?.query,
          status: (error as { status?: unknown })?.status,
          data: (error as { data?: unknown })?.data
        });
        return error;
      }
    }),
    getReports: builder.query<Report[], ReportFilters>({
      query: ({
        patientId,
        appointmentId: appointment = "",
        type,
        limit = 100,
        offset = 0,
        ordering = "-id",
        has_audiogram,
        has_real_ear_measurement,
        has_test_box,
        noahSession,
        query = GET_LIST_OF_REPORTS_DEFAULT_QUERY
      }) => ({
        url: `/reports/?${formatValidQueryParamUrlCommaSeparated({
          patient: patientId,
          type,
          appointment,
          ordering,
          limit,
          has_audiogram,
          has_real_ear_measurement,
          has_test_box,
          noah_session: noahSession,
          offset
        })}&query=${query}`,
        headers: { "X-Reports-DB": "true" }
      }),
      providesTags: (response, error, args) => [
        "Reports",
        { id: args.patientId, type: "Reports" }
      ],
      transformResponse: (response: ReportsResponse) =>
        response.map(convertReportResponseToModel)
    }),
    addReport: builder.mutation<Report, Partial<ReportInput>>({
      query: (reportData) => ({
        url: "/reports/",
        method: "POST",
        body: reportData
      }),
      invalidatesTags: ["Reports"]
    }),
    updateReport: builder.mutation<
      ReportResponse,
      Partial<ReportInput> & { id: number }
    >({
      query: ({ id, ...reportData }: ReportInput & { id: number }) => ({
        url: `/reports/${id}/`,
        method: "PATCH",
        body: reportData
      })
      //
      // Please do not invalidate tags here.
      // It refreshes the whole report in autosaving
      // which leads to really bad side effects
      //
      // invalidatesTags: (result) => {
      //   return [{ type: "Report", id: result?.id }];
      // },
    }),
    // Brings REM / Test Box reports of the same Noah session into an Audiology report. The target's
    // sections, every report list and the Noah sessions table all change shape.
    combineReports: builder.mutation<ReportResponse, CombineReportsInput>({
      query: ({ targetId, ...body }) => ({
        url: `/reports/${targetId}/combine/`,
        method: "POST",
        body
      }),
      invalidatesTags: (_result, _error, { targetId }) => [
        { type: "Report", id: targetId },
        "Reports",
        "NoahSessions"
      ]
    }),
    getReportCustomTests: builder.query<CustomTest[], number>({
      query: (reportId) => ({
        url: `/reports/${reportId}/custom-test-reports/?limit=300`,
        headers: { "X-Reports-DB": "true" }
      }),
      providesTags: ["CustomTests"],
      transformResponse: (response: CustomTestResponse[]) =>
        response.map(convertCustomTestResponseToModel)
    }),
    addCustomTest: builder.mutation<CustomTestResponse, CustomTestInput>({
      query: ({ report_id, ...customTestData }) => ({
        url: `/reports/${report_id}/custom-test-reports/`,
        method: "POST",
        body: customTestData
      }),
      invalidatesTags: ["CustomTests"]
    }),
    updateCustomTest: builder.mutation<
      CustomTestResponse,
      CustomTestInput & CustomTestResponse
    >({
      query: ({ report_id, id, ...customTestData }) => ({
        url: `/reports/${report_id}/custom-test-reports/${id}/`,
        method: "PATCH",
        body: customTestData
      }),
      invalidatesTags: ["CustomTests"]
    }),
    calculateReportDiagnosis: builder.mutation<Diagnosis, number>({
      query: (reportId) => ({
        url: `/reports/${reportId}/diagnosis/`,
        method: "PATCH"
      }),
      invalidatesTags: ["Appointments"]
    }),
    removeCustomTest: builder.mutation<void, { report_id: number; id: number }>(
      {
        query: ({ report_id, id }) => ({
          url: `/reports/${report_id}/custom-test-reports/${id}/`,
          method: "DELETE"
        }),
        invalidatesTags: ["CustomTests"]
      }
    ),
    deleteReport: builder.mutation<void, number>({
      query: (report_id) => ({
        url: `/reports/${report_id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Reports", "CustomTests"]
    }),
    syncReportFromNoah: builder.mutation<
      Report,
      { report_id: number; session_id?: number }
    >({
      query: ({ report_id, session_id }) => ({
        url: `/reports/${report_id}/sync`,
        params: {
          session_id
        },
        method: "PUT"
      }),
      invalidatesTags: (result, _, { session_id }) => {
        if (session_id) {
          return ["NoahSessions", "NoahDevices"];
        }

        return [];
      }
    }),
    autoCompleteReportSummary: builder.mutation<
      string,
      {
        report_id: number;
        user_prompt: string;
        format?: PromptOutputFormat;
        override_template_id?: number;
        override_source?: "general";
      }
    >({
      query: ({
        report_id,
        user_prompt,
        format,
        override_template_id,
        override_source
      }) => ({
        url: `/reports/${report_id}/complete`,
        method: "POST",
        body: {
          user_prompt,
          ...(format && { format }),
          ...(override_template_id != null && { override_template_id }),
          ...(override_source && { override_source })
        }
      })
    }),
    getReportSummaryInstructions: builder.query<
      ReportSummaryInstructions,
      number
    >({
      query: (reportId) => `/reports/${reportId}/summary-instructions`
    })
  })
});

export const {
  useGetReportQuery,
  useLazyGetReportQuery,
  useGetReportsQuery,
  useAddReportMutation,
  useUpdateReportMutation,
  useGetReportCustomTestsQuery,
  useAddCustomTestMutation,
  useUpdateCustomTestMutation,
  useRemoveCustomTestMutation,
  useSyncReportFromNoahMutation,
  useCombineReportsMutation,
  useAutoCompleteReportSummaryMutation,
  useDeleteReportMutation,
  useCalculateReportDiagnosisMutation,
  useGetReportSummaryInstructionsQuery
} = reportsApi;
